import {
  Box,
  VStack,
  HStack,
  Text,
  Icon,
  Badge,
  Spinner,
  Center,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Tag,
  Divider,
} from '@chakra-ui/react'
import { LuShieldCheck, LuEye, LuDownload, LuClock } from 'react-icons/lu'
import { formatDistanceToNow } from 'date-fns'
import { fr } from 'date-fns/locale'
import { useQuery } from '@tanstack/react-query'
import axiosClient from '../../api/axios'
import { useTranslation } from 'react-i18next'

interface AccessLogEntry {
  id: string
  action: 'view_case' | 'export_case' | string
  resourceType: string
  resourceId: string | null
  metadata: { format?: string; [k: string]: any } | null
  createdAt: string
  viewer: {
    id: string
    email: string | null
    contactName: string | null
    companyName: string | null
    role: string
  }
}

const ACTION_META: Record<string, { label: string; icon: any; scheme: string }> = {
  view_case: { label: 'Consultation', icon: LuEye, scheme: 'blue' },
  export_case: { label: 'Export', icon: LuDownload, scheme: 'purple' },
}

const RESOURCE_LABEL: Record<string, string> = {
  blot_case: 'profile.accessLog.resourceBlotCase',
  malaria_case: 'profile.accessLog.resourceMalariaCase',
  user_summary: 'profile.accessLog.resourceUserSummary',
}

/**
 * RGPD Art. 13(1)(e) + 15(1)(c) — destinataires des données.
 *
 * Affiche au user le journal des accès en lecture effectués par le personnel
 * Careflai sur ses données (admin-shadow). Données fetchées depuis
 * GET /api/users/me/access-log et triées par date décroissante.
 *
 * Self-contained — peut être déposé tel quel dans n'importe quelle page
 * profil / settings.
 */
export default function AccessLogPanel() {
  const { t } = useTranslation()
  const { data, isLoading, error } = useQuery({
    queryKey: ['me', 'access-log'],
    queryFn: async () => {
      const { data } = await axiosClient.get<{ count: number; entries: AccessLogEntry[] }>(
        '/users/me/access-log',
        { params: { limit: 100 } },
      )
      return data
    },
    staleTime: 60_000,
  })

  return (
    <Box bg="white" borderRadius="2xl" boxShadow="lg" p={{ base: 5, md: 7 }}>
      <HStack spacing={3} mb={1}>
        <Box w="44px" h="44px" borderRadius="xl" bgGradient="linear(135deg, purple.100, pink.100)" display="grid" sx={{ placeItems: 'center' }}>
          <Icon as={LuShieldCheck} boxSize={5} color="purple.600" />
        </Box>
        <VStack align="start" spacing={0}>
          <Text fontWeight="700" fontSize="lg" color="gray.800">{t('profile.accessLog.title')}</Text>
          <Text fontSize="xs" color="gray.500">{t('profile.accessLog.subtitle')}</Text>
        </VStack>
      </HStack>
      <Divider my={4} />
      <Text fontSize="xs" color="gray.500" mb={4} lineHeight="1.55">
        Conformément à l'art. 15 du RGPD, voici la liste des accès en lecture effectués
        par le personnel Careflai sur vos cas. Ces accès se font dans le cadre du support
        technique et de l'amélioration produit — toute modification reste exclusivement
        à votre main.
      </Text>

      {isLoading && (
        <Center py={10}><Spinner color="purple.500" /></Center>
      )}

      {error && (
        <Text color="red.500" fontSize="sm">
          Erreur de chargement : {(error as Error).message}
        </Text>
      )}

      {data && data.count === 0 && (
        <Center py={8} bg="green.50" borderRadius="xl" border="1px dashed" borderColor="green.200">
          <VStack spacing={1}>
            <Icon as={LuShieldCheck} boxSize={8} color="green.500" />
            <Text fontSize="sm" color="green.700" fontWeight="600">{t('profile.accessLog.none')}</Text>
            <Text fontSize="xs" color="green.600">{t('profile.accessLog.noneDetail')}</Text>
          </VStack>
        </Center>
      )}

      {data && data.count > 0 && (
        <Box overflowX="auto" border="1px solid" borderColor="gray.100" borderRadius="lg">
          <Table size="sm" variant="simple">
            <Thead bg="gray.50">
              <Tr>
                <Th fontSize="2xs">{t('profile.accessLog.when')}</Th>
                <Th fontSize="2xs">{t('profile.accessLog.who')}</Th>
                <Th fontSize="2xs">Action</Th>
                <Th fontSize="2xs">{t('profile.accessLog.resource')}</Th>
                <Th fontSize="2xs">Format</Th>
              </Tr>
            </Thead>
            <Tbody>
              {data.entries.map((e) => {
                const am = ACTION_META[e.action] ?? { label: e.action, icon: LuEye, scheme: 'gray' }
                return (
                  <Tr key={e.id} _hover={{ bg: 'gray.50' }}>
                    <Td fontSize="xs">
                      <HStack spacing={1.5}>
                        <Icon as={LuClock} boxSize={3} color="gray.400" />
                        <Text>{formatDistanceToNow(new Date(e.createdAt), { addSuffix: true, locale: fr })}</Text>
                      </HStack>
                    </Td>
                    <Td fontSize="xs">
                      <VStack align="start" spacing={0}>
                        <Text fontWeight="600">{e.viewer.contactName ?? e.viewer.email ?? '—'}</Text>
                        <Text color="gray.500" fontSize="2xs">{e.viewer.companyName ?? 'Careflai'} · {e.viewer.role}</Text>
                      </VStack>
                    </Td>
                    <Td>
                      <Badge colorScheme={am.scheme} variant="subtle" borderRadius="full" px={2} fontSize="2xs">
                        <HStack spacing={1}>
                          <Icon as={am.icon} boxSize={3} />
                          <Text>{am.label}</Text>
                        </HStack>
                      </Badge>
                    </Td>
                    <Td fontSize="xs">{RESOURCE_LABEL[e.resourceType] ? t(RESOURCE_LABEL[e.resourceType]) : e.resourceType}</Td>
                    <Td>
                      {e.metadata?.format
                        ? <Tag size="sm" variant="subtle" colorScheme="gray">{e.metadata.format.toUpperCase()}</Tag>
                        : <Text fontSize="2xs" color="gray.400">—</Text>}
                    </Td>
                  </Tr>
                )
              })}
            </Tbody>
          </Table>
        </Box>
      )}
    </Box>
  )
}
